package dev.akshaychordiya

import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.color

/**
 * One colour mode's worth of the site palette.
 *
 * @param background The page. Never pure white or pure black.
 * @param text Body copy.
 * @param surface Containers that separate from the page without becoming a hard-edged box.
 * @param border Hairlines and dividers.
 * @param subdued De-emphasised text - dates, captions, metadata.
 * @param brandPrimary Links, active states, map dots.
 * @param brandAccent A second, warmer highlight - the "work in progress" badge, occasional emphasis.
 */
class SitePalette(
    val background: Color,
    val text: Color,
    val surface: Color,
    val border: Color,
    val subdued: Color,
    val brandPrimary: Color,
    val brandAccent: Color,
)

/**
 * A complete theme: a light palette and its dark counterpart.
 *
 * Every foreground value in both palettes clears WCAG AA (4.5:1) against its own background - see the per-theme
 * tables below. If you change a colour, re-check the ratio; `subdued` and `brandPrimary` in light mode are the two
 * that sit closest to the line.
 */
class SiteTheme(val name: String, val light: SitePalette, val dark: SitePalette)

object SiteThemes {
    /**
     * Cool slate neutrals with a confident azure. Crisp and legible; the register most well-made developer tooling
     * sites use.
     *
     * | Role         | Light   | Dark    |
     * |--------------|---------|---------|
     * | text         | 15.6:1  | 15.5:1  |
     * | subdued      |  5.5:1  |  7.0:1  |
     * | brandPrimary |  4.7:1  |  8.5:1  |
     * | brandAccent  |  4.7:1  | 10.1:1  |
     */
    val Azure = SiteTheme(
        name = "azure",
        light = SitePalette(
            background = Color.rgb(0xF7F9FB),
            text = Color.rgb(0x16202B),
            surface = Color.rgb(0xEDF1F6),
            border = Color.rgb(0xDCE3EC),
            subdued = Color.rgb(0x5A6675),
            brandPrimary = Color.rgb(0x1D6FD0),
            brandAccent = Color.rgb(0xA85B00),
        ),
        dark = SitePalette(
            background = Color.rgb(0x0E1319),
            text = Color.rgb(0xE4EAF2),
            surface = Color.rgb(0x171D26),
            border = Color.rgb(0x28303B),
            subdued = Color.rgb(0x93A0B1),
            brandPrimary = Color.rgb(0x6FB2FF),
            brandAccent = Color.rgb(0xF0B360),
        ),
    )

    /**
     * Warm-neutral base with Android's green as the accent, darkened in light mode so it stays readable as link
     * text - the stock Android green (#3DDC84) is far too light for that.
     *
     * | Role         | Light   | Dark    |
     * |--------------|---------|---------|
     * | text         | 16.0:1  | 15.4:1  |
     * | subdued      |  5.6:1  |  6.9:1  |
     * | brandPrimary |  5.1:1  | 10.3:1  |
     * | brandAccent  |  5.1:1  |  9.1:1  |
     */
    val AndroidGreen = SiteTheme(
        name = "android-green",
        light = SitePalette(
            background = Color.rgb(0xFAFAF7),
            text = Color.rgb(0x1B1F1B),
            surface = Color.rgb(0xF0F1EC),
            border = Color.rgb(0xE1E3DA),
            subdued = Color.rgb(0x5F665D),
            brandPrimary = Color.rgb(0x1F7A4D),
            brandAccent = Color.rgb(0xA8541A),
        ),
        dark = SitePalette(
            background = Color.rgb(0x111411),
            text = Color.rgb(0xE7EAE4),
            surface = Color.rgb(0x1A1F1A),
            border = Color.rgb(0x2A312A),
            subdued = Color.rgb(0x98A094),
            brandPrimary = Color.rgb(0x4ED88F),
            brandAccent = Color.rgb(0xE8A85F),
        ),
    )
}

/**
 * The palette in force.
 *
 * Swap this one line and rebuild to switch the whole site over - nothing else references a theme directly.
 */
val activeTheme: SiteTheme = SiteThemes.AndroidGreen

fun ColorMode.toSitePalette(): SitePalette = when (this) {
    ColorMode.LIGHT -> activeTheme.light
    ColorMode.DARK -> activeTheme.dark
}

@InitSilk
fun initTheme(ctx: InitSilkContext) {
    ctx.theme.palettes.light.background = activeTheme.light.background
    ctx.theme.palettes.light.color = activeTheme.light.text
    ctx.theme.palettes.dark.background = activeTheme.dark.background
    ctx.theme.palettes.dark.color = activeTheme.dark.text
}
